import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ImageSizeTypeEnum, ImageTypeNameEnum } from '@inter-ikea-kompis/enums';
import { MeasurementSystemEnum } from '@inter-ikea-kompis/enums';
import type { ILocalizedInformation, IProduct, ITheme } from '@inter-ikea-kompis/types';
import {
	DexfSettingsUtility,
	ProductDescriptionProductMeasureEnum,
	ProductDescriptionUtility
} from '@inter-ikea-kompis/utilities';
import { ProductUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';
import type { IKompisElementPropertyValue } from '@inter-ikea-kompis/web-component';

import { Image } from '@inter-ikea-kompis/component-image';

import { Text } from '@ingka/common-styles-webc';

import type IProductInformationSectionDexfSettings from '../../types/IProductInformationSectionDexfSettings.js';
import type IProductInformationSectionTranslations from '../../types/IProductInformationSectionTranslations.js';
import MeasurementsCardStyle from './MeasurementsCardStyle.js';

/**
 * This is the MeasurementsCard sub component to Product Information Section.
 *
 * Used to display all different measurements along with a illustration of the measurements.
 */

export default class MeasurementsCard extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IProductInformationSectionTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IProductInformationSectionDexfSettings | null = null;

	// Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
	@property({ type: String })
	public measurementSystem: MeasurementSystemEnum | null = null;

	// A product with the type IProduct.
	@property({ type: Object, required: true })
	public product!: IProduct;

	private dexfSettings: IProductInformationSectionDexfSettings | null = null;

	public override connectedCallback(): void {
		this.customElements.define('kompis-image', Image);
		super.connectedCallback();
	}

	public override render(): RenderResult {
		// We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslationsAndSettings()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		const headingTextClass = Text.classnames({ heading: { m: true } });

		return html`
			<style>
				${MeasurementsCardStyle.getStyle({ theme: this.theme })}
			</style>

			<div class="container">
				<h2 class="header ${headingTextClass}">
					${this.getTranslations().productCardMeasurementTitle}
				</h2>

				<div class="measurements">${this.getProductMeasurements()}</div>
				${this.getProductMeasurementIllustration()}
			</div>
		`;
	}

	protected override shouldUpdate(
		changedProperties: Map<string, IKompisElementPropertyValue>
	): boolean {
		if (
			changedProperties.has('localizedInformation') &&
			this.localizedInformation &&
			!this.settings
		) {
			this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
		}
		if (changedProperties.has('settings') && this.settings) {
			this.dexfSettings = this.settings;
		}

		return true;
	}

	private hasTranslationsAndSettings(): boolean {
		return (!!this.translations && !!this.settings) || !!this.localizedInformation;
	}

	private getTranslations(): IProductInformationSectionTranslations {
		return this.translations
			? this.translations
			: <IProductInformationSectionTranslations>this.localizedInformation?.translations;
	}

	private getSettings(): IProductInformationSectionDexfSettings {
		return <IProductInformationSectionDexfSettings>this.dexfSettings;
	}

	private getProductMeasurements(): RenderResult {
		const productMeasurements = ProductDescriptionUtility.getProductMeasures({
			settings: this.getSettings(),
			product: this.product,
			productMeasures: ProductDescriptionProductMeasureEnum.array,
			measurementSystem: this.measurementSystem
		}) as { typeName: string; measure: string }[];

		if (!productMeasurements) {
			return null;
		}

		const headingTextClass = Text.classnames({ heading: { xs: true } });
		const bodyTextClass = Text.classnames({ body: { m: true } });

		return productMeasurements.map(({ typeName, measure }) => {
			return html`
				<span class="${bodyTextClass}">
					<span class="typeName ${headingTextClass}">${typeName}:</span>
					${measure}
				</span>
			`;
		});
	}

	private constructAltTextFromProduct(): string {
		if (!this.product?.content) {
			return '';
		}

		const { name, typeName, validDesignText, measureReference } = this.product.content;
		const localisation = this.getSettings().localisation;

		const altText = (useMetric: boolean): string => {
			return `
			${name} ${typeName} ${validDesignText}, ${
				useMetric
					? `${measureReference?.textMetric ?? ''}`
					: `${measureReference?.textImperial ?? ''}`
			}
		`.trim();
		};

		if (this.measurementSystem) {
			return altText(this.measurementSystem === MeasurementSystemEnum.metric);
		}

		return altText(localisation.useMetricMeasures);
	}

	private getProductMeasurementIllustration(): RenderResult {
		const productImages = ProductUtility.getProductImages(
			this.product,
			ImageTypeNameEnum.measurementIllustration
		).filter((productImage) => productImage.size === ImageSizeTypeEnum.S5);

		if (!productImages || productImages.length === 0) {
			return null;
		}

		if (productImages.length === 1) {
			return html`
				<kompis-image .src="${productImages[0].url}" .alt="${this.constructAltTextFromProduct()}">
				</kompis-image>
			`;
		} else {
			return html`
				<div class="images">
					${productImages.map((image) => {
						return html`
							<kompis-image .src="${image.url}" .alt="${this.constructAltTextFromProduct()}">
							</kompis-image>
						`;
					})}
				</div>
			`;
		}
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-measurements-card': MeasurementsCard;
	}
}
